import { ArrowLeft, ArrowRight, Calendar } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "../ui/button";
import { useLanguage } from "../../contexts/LanguageContext";

const MAZAL_IMAGE = "https://customer-assets.emergentagent.com/job_e84fca2b-7e76-4834-b103-9bf7c18b7fcd/artifacts/5cqkqprr_%D7%9E%D7%96%D7%99%2004.png";

export const HeroSection = () => {
  const { language, direction, translations: t } = useLanguage();
  const ArrowIcon = direction === 'rtl' ? ArrowLeft : ArrowRight;
  
  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-28 pb-8 overflow-hidden bg-[#FAF7F2] scroll-mt-20" data-testid="hero-section" aria-labelledby="hero-heading">
      <div className="max-w-7xl mx-auto px-4 md:px-8 w-full relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center mb-12">
          <motion.div
            className="text-center lg:text-right order-2 lg:order-1"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.span
              className="text-[#C4956A] font-semibold text-sm tracking-wider block mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              {language === 'he' ? 'מזל גניאל — קוסמטיקאית פרא-רפואית' : 'Mazal Ganiel — Paramedical Cosmetician'}
            </motion.span>

            <motion.h1
              className="font-['Heebo'] text-4xl md:text-5xl lg:text-[3.5rem] font-bold text-[#2C2C2C] leading-tight mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {language === 'he' ? (
                <>
                  40 שנה של מומחיות.
                  <br />
                  <span className="text-[#C4956A]">מטפלת אחת.</span>
                </>
              ) : (
                <>
                  40 years of expertise.
                  <br />
                  <span className="text-[#C4956A]">One specialist.</span>
                </>
              )}
            </motion.h1>

            <motion.p
              className="text-lg md:text-xl text-[#2C2C2C]/70 mb-8 max-w-xl mx-auto lg:mx-0 lg:mr-0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              {language === 'he' 
                ? 'מומחית בטיפול צלקות מורכבות, צלקות ניתוח, כוויות ואקנה. ייעוץ ראשון חינם.'
                : 'Specialist in complex scars, surgical scars, burns and acne. First consultation free.'}
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <a href="#contact">
                <Button size="lg" className="rounded-full px-8 py-6 bg-[#C4956A] hover:bg-[#b38559] hover:scale-[1.02] text-white text-lg shadow-lg shadow-[#C4956A]/30 transition-all" data-testid="hero-cta-primary">
                  {t.hero.cta1}
                  <ArrowIcon className="w-5 h-5 mx-2" />
                </Button>
              </a>
              <a href="#results">
                <Button size="lg" variant="outline" className="rounded-full px-8 py-6 border-2 border-[#2C2C2C] text-[#2C2C2C] hover:bg-[#2C2C2C] hover:text-white hover:scale-[1.02] text-lg transition-all" data-testid="hero-cta-secondary">
                  {t.hero.cta2}
                </Button>
              </a>
            </motion.div>
          </motion.div>

          <motion.div 
            className="order-1 lg:order-2 flex justify-center lg:justify-start"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="relative w-full max-w-lg">
              <div className="relative rounded-2xl shadow-2xl overflow-hidden bg-[#E8DDD0]">
                <img
                  src={MAZAL_IMAGE}
                  alt={language === 'he' ? "מזל גניאל - קוסמטיקאית פרא-רפואית עם 40 שנות ניסיון" : "Mazal Ganiel - Paramedical cosmetician with 40 years experience"}
                  className="w-full h-full object-cover"
                  loading="eager"
                  decoding="async"
                  fetchPriority="high"
                  width="512"
                  height="640"
                  data-testid="hero-mazal-image"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-xl">
                <div className="text-3xl font-bold text-[#C4956A]">40+</div>
                <div className="text-xs text-[#2C2C2C]/70">
                  {language === 'he' ? 'שנות ניסיון' : 'Years experience'}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
        
        <motion.div
          className="flex justify-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <a href="/scar-checker" className="group block w-full max-w-2xl" data-testid="scar-checker-link">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#2C2C2C] to-[#1a1a1a] p-6 border border-[#C4956A]/30 hover:border-[#C4956A] transition-all duration-300 hover:shadow-xl hover:shadow-[#C4956A]/20 hover:-translate-y-1">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-40 bg-[#C4956A]/10 rounded-full blur-3xl group-hover:bg-[#C4956A]/20 transition-all"></div>
              <div className="relative flex flex-col sm:flex-row items-center text-center sm:text-right gap-4 sm:gap-6">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#C4956A] to-[#b38559] flex items-center justify-center shadow-lg flex-shrink-0">
                  <Calendar className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-bold text-xl mb-1 group-hover:text-[#C4956A] transition-colors">
                    {language === 'he' ? 'קבעי פגישת ייעוץ חינם' : 'Book a Free Consultation'}
                  </h4>
                  <p className="text-white/60 text-sm">
                    {language === 'he' ? 'ספרי לנו על הצלקת שלך ונחזור אלייך לתיאום פגישה' : 'Tell us about your scar and we\'ll contact you to schedule'}
                  </p>
                </div>
                <div className="flex items-center gap-2 sm:flex-col sm:items-end">
                  <span className="text-[#C4956A] text-xs font-medium px-3 py-1.5 bg-[#C4956A]/10 rounded-full whitespace-nowrap">
                    {language === 'he' ? '⏱️ פחות מדקה' : '⏱️ Less than a minute'}
                  </span>
                  <span className="text-green-400 text-xs font-medium px-3 py-1.5 bg-green-400/10 rounded-full whitespace-nowrap">
                    {language === 'he' ? 'ללא התחייבות' : 'No obligation'}
                  </span>
                </div>
              </div>
            </div>
          </a>
        </motion.div>
      </div>
    </section>
  );
};
